import React from 'react';
import ReactDOM from 'react-dom/client';
import * as Sentry from '@sentry/react';
import './index.css';
import reportWebVitals from './reportWebVitals';
import { Provider } from 'react-redux';
import { store } from 'store';
import { library } from '@fortawesome/fontawesome-svg-core';
import Router from 'routes';
import { TokenValidator } from 'components/TokenValidator';
import { SocketProvider } from 'components/SocketProvider';
import { ImportModalManager } from 'components/ImportModalManager/ImportModalManager';
import { ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { HelmetProvider } from '@dr.pogodin/react-helmet';
import logger from './utils/logger';
import { initAudioManager } from './utils/audioManager';
import {
  faChevronDown,
  faEye,
  faEyeSlash,
  faMagnifyingGlass,
  faUser,
  faPlus,
  faMinus,
  faTimes,
  faFileImport,
  faShare,
  faTrash,
  faCheckCircle,
  faForward,
  faTimesCircle,
  faCloudUploadAlt,
  faFileCsv,
  faCirclePlus,
  faRightLeft,
  faLock,
} from '@fortawesome/free-solid-svg-icons';
import { ThemeProvider } from '@mui/material';
import { theme } from 'theme';
import { SidebarCollapseProvider } from 'context/SidebarCollapseContext';
library.add(
  faEye,
  faEyeSlash,
  faMagnifyingGlass,
  faUser,
  faChevronDown,
  faPlus,
  faMinus,
  faTimes,
  faFileImport,
  faShare,
  faTrash,
  faCheckCircle,
  faForward,
  faTimesCircle,
  faCloudUploadAlt,
  faFileCsv,
  faCirclePlus,
  faRightLeft,
  faLock
);

// Install fetch interceptor for debugging impersonation
logger.debug('🌐 Installing fetch interceptor...');

const originalFetch = window.fetch;
window.fetch = async function(...args: Parameters<typeof fetch>) {
  // Only log during impersonation
  if (typeof window !== 'undefined' && localStorage.getItem('isImpersonating') === 'true') {
    logger.debug('🌐 ACTUAL FETCH CALL (INTERCEPTED)');

    // RTK Query passes a Request object as first argument, not (url, options)
    const requestOrUrl = args[0];

    if (requestOrUrl instanceof Request) {
      // It's a Request object - headers are inside it
      logger.debug('Request format: Request object');
      logger.debug('URL:', requestOrUrl.url);
      logger.debug('Method:', requestOrUrl.method);
      logger.debug('Headers object:', requestOrUrl.headers);

      // Read the Authorization header from the Request
      const authHeader = requestOrUrl.headers.get('Authorization');
      logger.debug('Authorization header:', authHeader);
      logger.debug('Authorization first 50 chars:', authHeader?.substring(0, 50));
      logger.debug('Authorization exists?', !!authHeader);

      // Show all headers
      logger.debug('All headers:', Array.from(requestOrUrl.headers.entries()));
    } else {
      // Standard fetch format: (url, options)
      logger.debug('Request format: Standard (url, options)');
      logger.debug('URL:', requestOrUrl);
      logger.debug('Options:', args[1]);

      const options = args[1];
      if (options?.headers) {
        if (options.headers instanceof Headers) {
          logger.debug('Authorization (Headers.get):', options.headers.get('Authorization'));
          logger.debug('All headers:', Array.from(options.headers.entries()));
        } else {
          logger.debug('Authorization (object):', (options.headers as any)['Authorization']);
          logger.debug('All headers:', Object.keys(options.headers));
        }
      } else {
        logger.debug('⚠️ No headers object at all!');
      }
    }
  }

  return originalFetch.apply(this, args as [RequestInfo | URL, RequestInit?]);
};

logger.debug('✅ Fetch interceptor installed');

// Initialize Sentry for error tracking and performance monitoring (production only)
Sentry.init({
  dsn: 'https://b78b70f3f145f80b6ff5c03e0fc51220@o4510620165931008.ingest.us.sentry.io/4510620256960512',
  environment: process.env.REACT_APP_ENV || 'development',
  release: process.env.REACT_APP_SENTRY_RELEASE,
  sendDefaultPii: true,
  enabled: process.env.NODE_ENV === 'production',
  integrations: [Sentry.browserTracingIntegration()],
  tracesSampleRate: 0.1, // Capture 10% of transactions for performance monitoring
});

// Initialize audio manager for point balance sounds
initAudioManager();

const root = ReactDOM.createRoot(
  document.getElementById('root') as HTMLElement
);

root.render(
  <React.StrictMode>
    <Sentry.ErrorBoundary fallback={<p>An error occurred. Please refresh the page.</p>}>
      <HelmetProvider>
        <Provider store={store}>
          <ThemeProvider theme={theme}>
            <SidebarCollapseProvider>
            <TokenValidator>
              <SocketProvider>
                <Router />
                <ImportModalManager />
              </SocketProvider>
              <ToastContainer
                position="top-right"
                autoClose={5000}
                hideProgressBar={false}
                newestOnTop={false}
                closeOnClick
                rtl={false}
                pauseOnFocusLoss
                draggable
                pauseOnHover
                theme="dark"
                limit={3}
                style={{ top: '70px' }}
              />
            </TokenValidator>
            </SidebarCollapseProvider>
          </ThemeProvider>
        </Provider>
      </HelmetProvider>
    </Sentry.ErrorBoundary>
  </React.StrictMode>
);

// If you want to start measuring performance in your app, pass a function
// to log results (for example: reportWebVitals(console.log))
// or send to an analytics endpoint. Learn more: https://bit.ly/CRA-vitals
reportWebVitals();
